import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Only English is bundled for instant first paint.
// All other locales are lazy-loaded on demand.
import en from './locales/en.json';

import {
   SUPPORTED_LANGUAGE_CODES,
   DEFAULT_LANGUAGE,
} from './languages';
import { LANGUAGE_ACTIVE_STORAGE_KEY } from './resolveLanguage';

const NS = 'common';

// Lazy-load locale files via dynamic import.
const localeLoaders = {
   'en-US': () => import('./locales/en-US.json'),
   fr: () => import('./locales/fr.json'),
   es: () => import('./locales/es.json'),
   de: () => import('./locales/de.json'),
   pt: () => import('./locales/pt.json'),
   it: () => import('./locales/it.json'),
   no: () => import('./locales/no.json'),
   pl: () => import('./locales/pl.json'),
   nl: () => import('./locales/nl.json'),
};

// Cache to avoid re-fetching already-loaded locales.
const loadedLocales = new Set(['en']);

// Load a locale's translations into i18next on demand.
export const loadLocale = async (lang) => {
   if (loadedLocales.has(lang) || !localeLoaders[lang]) return;
   const module = await localeLoaders[lang]();
   i18n.addResourceBundle(lang, NS, module.default || module, true, true);
   loadedLocales.add(lang);
};

i18n
   .use(LanguageDetector)
   .use(initReactI18next)
   .init({
      resources: {
         en: { [NS]: en },
      },
      supportedLngs: SUPPORTED_LANGUAGE_CODES,
      fallbackLng: DEFAULT_LANGUAGE,
      defaultNS: NS,
      ns: [NS],
      interpolation: { escapeValue: false },
      detection: {
         order: ['localStorage', 'navigator'],
         lookupLocalStorage: LANGUAGE_ACTIVE_STORAGE_KEY,
         caches: ['localStorage'],
      },
      returnEmptyString: false,
      react: {
         useSuspense: false,
      },
   });

// If the detected language on init is not bundled English, load it immediately.
const detectedLang = i18n.language;
if (detectedLang && detectedLang !== 'en') {
   // Try exact match first (e.g. "en-US"), then 2-letter fallback
   const key = localeLoaders[detectedLang] ? detectedLang : detectedLang.slice(0, 2);
   if (localeLoaders[key]) loadLocale(key);
}

export default i18n;
